import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import PhoneInput from 'react-phone-number-input';
import { useDispatch, useSelector } from 'react-redux';
import CreatableSelect from 'react-select/creatable';
import CustomAlert from '../../components/CustomAlert';
import config from '../../config/Constant';
import { postRequest } from '../../helpers/apiHandler';
import { reloadContactList } from '../../store/actions/reduxAction';
import CloseIcon from '../SVG/CloseIcon';

const ContactDetails = ({ data, allData, setData, allTags, modalDetailShow, setModalDetailShow }) => {
  const dispatch = useDispatch();
  const { access_token } = useSelector((state) => state.auth.user);
  const [contactData, setContactData] = useState({ phone: '', country: '', date: '', tags: [], custom_field: {} });
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [alertState, setAlertState] = useState({ show: false, variant: 'danger', message: '' });

  useEffect(() => {
    if (modalDetailShow) {
      setContactData(data);
      setSelectedTags(data.tags.map(tag => ({ value: tag, label: tag })));
      setTagOptions(allTags);
    }
  }, [modalDetailShow, data, allTags]);

  useEffect(() => {
    if (alertState.show) {
      const timer = setTimeout(() => {
        setAlertState({ message: '', show: false, variant: '' });
      }, config.notify_time);
      return () => clearTimeout(timer);
    }
  }, [alertState]);

  const updateContact = useCallback((updatedData) => {
    postRequest('/v2/contacts-update-details', updatedData, access_token.token, (res) => {
      if (res.success) {
        dispatch(reloadContactList());
        setAlertState({
          message: 'Contact successfully saved',
          show: true,
          variant: 'success',
        });
      } else {
        setAlertState({
          message: res.message || 'There was an error saving contact',
          show: true,
          variant: 'danger',
        });
      }
    });
  }, [access_token.token, dispatch]);

  const debouncedUpdateContact = useCallback(
    debounce((updatedData) => {
      updateContact(updatedData);
    }, 2000),
    [updateContact]
  );

  const handleUpdateContact = useCallback((updatedField, value) => {
    const updatedData = { ...contactData, [updatedField]: value };
    setContactData(updatedData);
    setData(allData.map(item => item.id === updatedData.id ? updatedData : item));
    debouncedUpdateContact(updatedData);
  }, [contactData, allData, setData, debouncedUpdateContact]);

  const handleTagsChange = (selectedOptions, actionMeta) => {

    if (actionMeta.action === "remove-value") {
      const updatedTags = contactData.tags.filter(tag => tag !== actionMeta.removedValue.value);
      setContactData({ ...contactData, ...contactData.tags = [...updatedTags] })
      setSelectedTags(updatedTags.map(tag => ({ value: tag, label: tag })));
      handleUpdateContact("remove_tag", actionMeta.removedValue.value);
    }

    if (actionMeta.action === "select-option") {
      setContactData({ ...contactData, ...contactData.tags = [...contactData.tags, actionMeta.option.value] })
      setSelectedTags([...selectedTags, { value: actionMeta.option.value, label: actionMeta.option.value }]);
      handleUpdateContact("add_tag", actionMeta.option.value)
    }

    if (selectedOptions === null) setSelectedTags([]);

    if (selectedOptions !== null) {
      selectedOptions.map(option => option.value);
      setSelectedTags(selectedOptions);
    }
  };

  const handleTagsCreate = (tagName) => {
    const trimmedTagName = tagName.trim();
    if (trimmedTagName) {
      setSelectedTags([...selectedTags, { value: trimmedTagName, label: trimmedTagName }]);
      setTagOptions([...tagOptions, trimmedTagName]);
      setContactData({ ...contactData, ...contactData.tags = [trimmedTagName, ...contactData.tags] })
      handleUpdateContact("add_tag", trimmedTagName)
    }
  }

  const renderMetaFields = () => {
    const custom_fields = Object.keys(contactData.custom_field || {});
    return custom_fields.map((key) => (
      <Form.Group key={key}>
        <Form.Label>{key.replace('_', ' ').toUpperCase()}</Form.Label>
        <Form.Control
          value={contactData.custom_field[key]}
          onChange={(e) => handleUpdateContact('custom_field', { ...contactData.custom_field, [key]: e.target.value })}
        />
      </Form.Group>
    ));
  };

  return (
    <>
      <CustomAlert
        message={alertState.message}
        show={alertState.show}
        variant={alertState.variant}
        closeAlert={() => setAlertState({ show: false, variant: 'danger', message: '' })}
      />
      <Modal
        show={modalDetailShow}
        animation={true}
        className="modal-overview-detail"
        onHide={() => setModalDetailShow(false)}
      >
        <Modal.Body>
          <CloseIcon handleModalClose={() => setModalDetailShow(false)} />
          <Modal.Title as="h4" style={{ margin: '8px 0 16px' }}>
            Contact Details
          </Modal.Title>
          <div className="contact-wrapper">
            <div className="mobile-number">
              <div>ID: {contactData.id}</div>
            </div>
          </div>
          <Form>
            <Form.Group>
              <Form.Label>Mobile Number</Form.Label>
              <div className="phone-number-wrapper">
                <PhoneInput
                  international
                  placeholder="Enter mobile number"
                  defaultCountry="MY"
                  value={contactData.phone}
                  onChange={(phone) => handleUpdateContact('phone', phone)}
                />
              </div>
            </Form.Group>
            <Form.Group>
              <Form.Label>Tags</Form.Label>
              <CreatableSelect
                options={tagOptions.map(tag => ({ value: tag, label: tag }))}
                value={selectedTags}
                onChange={handleTagsChange}
                onCreateOption={handleTagsCreate}
                isMulti
                isClearable={false}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Country</Form.Label>
              <Form.Control
                value={contactData.country || 'MY'}
                onChange={(e) => handleUpdateContact('country', e.target.value)}
              />
            </Form.Group>
            {renderMetaFields()}
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ContactDetails;